import { z } from 'zod'

const periodPricing = z.object({
    amount: z.number(),
    overageAmount: z.number(),
})

const tierPricing = z.object({
    conversationLimit: z.number(),
    userLimit: z.number(),
    siteLimit: z.number(),
    messageLimit: z.number(),
    monthly: periodPricing.optional(),
    yearly: periodPricing.optional(),
})

export const pricing = z.object({
    currency: z.string(),
    publishableKey: z.string(),
    trial: z.object({
        days: z.number(),
        conversationLimit: z.number(),
    }),
    // Keyed by tier value (starter/growth/pro). Agency is contact-sales and
    // never present, so we key by string and read with an optional guard.
    tiers: z.record(z.string(), tierPricing),
    // An extra website per period in cents (null when not for sale). It adds no messages.
    extraWebsite: z.object({
        monthly: z.number().nullable(),
        yearly: z.number().nullable(),
    }),
    // An extra website message in cents, for example 4 for € 0.04.
    messageOverageAmount: z.number(),
    messageOverageDefaultLimit: z.number(),
    // New Website subscriptions start with candidates via WhatsApp on, up to this many per month.
    websiteOverageDefaultLimit: z.number(),
})

export type Pricing = z.infer<typeof pricing>
export type PeriodPricing = z.infer<typeof periodPricing>
export type TierPricing = z.infer<typeof tierPricing>

/**
 * Formats a Stripe unit amount (in cents) as a localised currency string, e.g.
 * 19700 → "€ 197" and 197 → "€ 1,97". Whole-euro amounts omit the decimals to
 * match how prices are presented across the pricing UI.
 */
export function formatPrice(cents: number, currency: string): string {
    const amount = cents / 100
    const hasFraction = amount % 1 !== 0

    return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2,
    }).format(amount)
}
